import { StateCreator } from 'zustand';
import { createLicenceKeeper } from '@umpry/licence';
import { verifyLicence, type Unlock } from '../../lib/licence';
import { loadLicenceCodes, saveLicenceCodes } from '../../services/licenceCodes';
import { registerLicenceCodes } from '../../services/accountGateService';
import type { AppStore } from '../index';

export interface LicenceSlice {
  licence: {
    /** What the codes redeemed on this phone unlock; undefined while the ones kept are checked. */
    unlocks: Unlock[] | undefined;
    /** Redeems a code: kept and true if it holds a licence, false and everything as it was if not. */
    redeem: (code: string) => Promise<boolean>;
  };
}

export const createLicenceSlice: StateCreator<AppStore, [], [], LicenceSlice> = (set) => {
  // What's kept, checked and added to is in @umpry/licence, the same as Blackbox's; this only
  // mirrors it for React.
  const keeper = createLicenceKeeper<Unlock>({ load: loadLicenceCodes, save: saveLicenceCodes, verify: verifyLicence });
  keeper.subscribe(() => set((state) => ({ licence: { ...state.licence, unlocks: keeper.getUnlocks() } })));

  // The server decides who may create an account; it learns of a code when it's redeemed, and
  // again on every start, for one redeemed while offline. Only codes that held up are sent.
  void keeper.whenChecked().then(() => {
    const codes = loadLicenceCodes();
    if (codes.length > 0) return registerLicenceCodes(codes);
  });

  return {
    licence: {
      unlocks: keeper.getUnlocks(),
      redeem: async (code) => {
        if (!(await keeper.redeem(code))) return false;
        // Waited for, so a sign-in straight after finds the server told; kept on the phone even if
        // the server can't be reached now.
        await registerLicenceCodes([code.replace(/\s/g, '')]);
        return true;
      },
    },
  };
};
